// src/components/TradeDetail/TradeDetail.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../Context/AuthContext';
import { io } from 'socket.io-client';
import { FiArrowLeft, FiClock, FiAlertOctagon, FiCheck, FiSend, FiPaperclip, FiUpload, FiUnlock, FiAlertTriangle, FiMessageCircle, FiUser, FiStar, FiUsers, FiLock, FiFileText, FiXCircle, FiX, FiShield, FiImage, FiArrowDown } from 'react-icons/fi';
import ReviewFlow from './ReviewFlow';
import PaymentAccountPicker from '../PaymentAccounts/PaymentAccountPicker';
import { apiFetch } from '../../services/apiClient';
import './TradeDetail.css';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:4000';

// Etiqueta de día para separadores del chat (Hoy / Ayer / fecha)
const dayLabel = (iso) => {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  const yest = new Date(now); yest.setDate(now.getDate() - 1);
  if (sameDay(d, now))  return 'Hoy';
  if (sameDay(d, yest)) return 'Ayer';
  return d.toLocaleDateString('es', { day: 'numeric', month: 'long', ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}) });
};

const STATUS_CONFIG = {
  CREATED:         { label: 'Creado',          color: '#888',    Icon: FiFileText,       step: 0 },
  WAITING_PAYMENT: { label: 'Esperando pago',  color: '#3b82f6', Icon: FiClock,          step: 1 },
  PAID:            { label: 'Pago enviado',    color: '#f59e0b', Icon: FiSend,           step: 2 },
  COMPLETED:       { label: 'Completado',      color: '#16a34a', Icon: FiCheck,          step: 3 },
  CANCELLED:       { label: 'Cancelado',       color: '#888',    Icon: FiXCircle,        step: -1 },
  DISPUTED:        { label: 'En disputa',      color: '#ef4444', Icon: FiAlertTriangle,  step: -1 },
};

const STEPS = [
  { label: 'Creado',         Icon: FiFileText },
  { label: 'Esperando pago', Icon: FiClock },
  { label: 'Pago enviado',   Icon: FiSend },
  { label: 'Completado',     Icon: FiCheck },
];

// ===========================
// TIMER — con callback al expirar
// ===========================
function PaymentTimer({ createdAt, status, onExpired }) {
  const [remaining, setRemaining] = useState(null);
  const expiredFired = useRef(false);

  useEffect(() => {
    if (status !== 'WAITING_PAYMENT') return;
    const deadline = new Date(createdAt).getTime() + 30 * 60 * 1000;

    const tick = () => {
      const diff = deadline - Date.now();
      if (diff <= 0) {
        setRemaining(0);
        if (!expiredFired.current) {
          expiredFired.current = true;
          onExpired?.();
        }
      } else {
        setRemaining(diff);
      }
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdAt, status]);

  if (status !== 'WAITING_PAYMENT' || remaining === null) return null;

  const mins    = Math.floor(remaining / 60000);
  const secs    = Math.floor((remaining % 60000) / 1000);
  const expired = remaining === 0;
  const urgent  = remaining < 5 * 60 * 1000;

  return (
    <div className={`td-timer ${urgent ? 'urgent' : ''} ${expired ? 'expired' : ''}`}>
      <span className="td-timer-icon">{expired ? <FiAlertOctagon /> : <FiClock />}</span>
      <div>
        <div className="td-timer-label">Tiempo para pagar</div>
        <div className="td-timer-value">
          {expired ? 'Tiempo agotado' : <span className="fin-num">{`${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`}</span>}
        </div>
      </div>
    </div>
  );
}

// ===========================
// INSTRUCCIONES POR ROL
// ===========================
function RoleInstructions({ isBuyer, status, trade }) {
  const instructions = {
    buyer: {
      WAITING_PAYMENT: {
        title: 'Tu turno — realiza el pago',
        steps: [
          `Transferí USDT ${Number(trade?.amount).toLocaleString()} al vendedor`,
          `Usá el método de pago acordado: ${trade?.payment}`,
          'Tomá captura del comprobante',
          'Subí el comprobante usando el botón de abajo',
        ],
        color: '#0091ea',
      },
      PAID: {
        title: 'Esperando confirmación del vendedor',
        steps: [
          'Tu comprobante fue enviado al vendedor',
          'El vendedor está verificando el pago',
          'Una vez confirmado, recibirás los fondos',
          'Si hay algún problema, contactá al soporte',
        ],
        color: '#f0b90b',
      },
      COMPLETED: {
        title: '¡Trade completado!',
        steps: ['El vendedor liberó los fondos', 'Ganaste +150 XP', 'Tu historial de trades fue actualizado'],
        color: '#00b248',
      },
    },
    seller: {
      WAITING_PAYMENT: {
        title: 'Esperando que el comprador pague',
        steps: [
          'El comprador está preparando el pago',
          `Método de pago: ${trade?.payment}`,
          'Recibirás una notificación cuando suba el comprobante',
          'No liberes fondos hasta verificar el pago',
        ],
        color: '#5a7a96',
      },
      PAID: {
        title: 'Verifica el comprobante de pago',
        steps: [
          'El comprador subió el comprobante de pago',
          'Verificá que el monto y los datos son correctos',
          'Confirmá en tu cuenta bancaria que recibiste el dinero',
          'Solo entonces presioná "Liberar fondos"',
        ],
        color: '#f0b90b',
      },
      COMPLETED: {
        title: '¡Trade completado!',
        steps: ['Liberaste los fondos correctamente', 'Ganaste +150 XP', 'Tu historial de trades fue actualizado'],
        color: '#00b248',
      },
    },
  };

  const role = isBuyer ? 'buyer' : 'seller';
  const info = instructions[role]?.[status];
  if (!info) return null;

  return (
    <div className="td-instructions" style={{ borderColor: info.color + '44' }}>
      <div className="td-instructions-title" style={{ color: info.color }}>{info.title}</div>
      <ol className="td-instructions-list">
        {info.steps.map((s, i) => <li key={i}>{s}</li>)}
      </ol>
    </div>
  );
}

// Vista previa del escrow 2-de-3 (futuro) + saldo demo (ledger simulado) + faucet de prueba.
// HOY es Nivel A (autocustodia): NO afirmar que Bitplify custodia fondos.
function EscrowBox({ trade }) {
  const [bal, setBal] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => { try { const r = await apiFetch('/api/me/balance'); if (r.ok) setBal(await r.json()); } catch {} };
  useEffect(() => { load(); }, []);
  const faucet = async () => {
    setLoading(true);
    try {
      const r = await apiFetch('/api/me/balance/faucet', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      if (r.ok) setBal(await r.json());
    } catch {}
    setLoading(false);
  };
  const held = trade?.escrowLocked && (trade.status === 'WAITING_PAYMENT' || trade.status === 'PAID');
  return (
    <div className="td-escrow">
      <div className="td-escrow-main">
        <FiLock className="td-escrow-ic" />
        {held
          ? <span><strong>USDT {Number(trade.amount).toLocaleString()}</strong> — vista previa del <strong>escrow 2-de-3</strong> (próximamente). Hoy operás P2P: tu plata vive en tu wallet, protegida por vendedores verificados, reputación y disputas.</span>
          : <span>Vista previa del <strong>escrow 2-de-3</strong> (próximamente). Hoy tu plata vive en tu wallet — Bitplify no la custodia.</span>}
      </div>
      {bal && (
        <div className="td-escrow-bal">
          <span>Tu saldo: <strong className="fin-num">{Number(bal.available || 0).toLocaleString()}</strong> disp. · <strong className="fin-num">{Number(bal.held || 0).toLocaleString()}</strong> retenido</span>
          <button className="td-escrow-faucet" onClick={faucet} disabled={loading}>{loading ? '…' : '+ Saldo demo'}</button>
        </div>
      )}
    </div>
  );
}

export default function TradeDetail() {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const { user }  = useAuth();
  const bottomRef    = useRef(null);
  const fileInputRef = useRef(null);
  const chatFileRef  = useRef(null);
  const messagesRef  = useRef(null);
  const socketRef    = useRef(null);

  const [trade, setTrade]             = useState(null);
  const [messages, setMessages]       = useState([]);
  const [text, setText]               = useState('');
  const [loading, setLoading]         = useState(true);
  const [sending, setSending]         = useState(false);
  const [releasing, setReleasing]     = useState(false);
  const [uploading, setUploading]     = useState(false);
  const [cancelling, setCancelling]   = useState(false);
  const [error, setError]             = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showDispute, setShowDispute] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');
  const [disputing, setDisputing]     = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [timerExpired, setTimerExpired] = useState(false);
  const [previewImg, setPreviewImg]   = useState(null);
  const [connected, setConnected]     = useState(false);
  const [chatImg, setChatImg]         = useState(null);   // { file, url } imagen a enviar por chat
  const [atBottom, setAtBottom]       = useState(true);   // chat scrolleado al final
  const [showReview, setShowReview]   = useState(false);  // mostrar formulario de reseña

  const token = localStorage.getItem('token');

  useEffect(() => {
    const socket = io(SOCKET_URL, { transports: ['websocket'], auth: { token } });
    socketRef.current = socket;
    socket.on('connect', () => { setConnected(true); socket.emit('join_trade', id); });
    socket.on('disconnect', () => setConnected(false));
    socket.on('new_message', (msg) => { setMessages(prev => prev.find(m => m.id === msg.id) ? prev : [...prev, msg]); });
    socket.on('trade_updated', (updated) => setTrade(updated));
    return () => { socket.emit('leave_trade', id); socket.disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchTrade = async () => {
    try {
      const res = await apiFetch('/api/trades');
      const all = await res.json();
      const found = all.find(t => String(t.id) === String(id));
      if (!found) throw new Error('Trade no encontrado');
      setTrade(found);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const res = await apiFetch(`/api/trades/${id}/messages`);
      setMessages(await res.json());
    } catch {}
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchTrade(); fetchMessages(); }, [id]);

  // Oculta el guía Arkana acá: en móvil se superpone con el botón de enviar del chat
  // y con los pasos del pago. Reusa el mecanismo del marketplace.
  useEffect(() => {
    document.body.classList.add('mk-hide-arkana');
    return () => document.body.classList.remove('mk-hide-arkana');
  }, []);
  // Auto-scroll solo si el usuario ya estaba al final (no interrumpe si está leyendo arriba)
  useEffect(() => { if (atBottom) bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]); // eslint-disable-line react-hooks/exhaustive-deps

  const onMessagesScroll = () => {
    const el = messagesRef.current;
    if (!el) return;
    setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 90);
  };
  const jumpToLatest = () => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); setAtBottom(true); };

  const handleSend = async () => {
    if (!text.trim() && !chatImg) return;
    setSending(true);
    try {
      if (chatImg) {
        const fd = new FormData();
        fd.append('image', chatImg.file);
        if (text.trim()) fd.append('content', text.trim());
        await apiFetch(`/api/trades/${id}/chat-image`, {
          method: 'POST',
          body: fd,
        });
        setChatImg(null);
        if (chatFileRef.current) chatFileRef.current.value = '';
      } else {
        await apiFetch(`/api/trades/${id}/messages`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content: text.trim() }),
        });
      }
      setText('');
      setAtBottom(true);
    } catch {}
    setSending(false);
  };

  const handleChatFile = (e) => {
    const file = e.target.files[0];
    if (file) setChatImg({ file, url: URL.createObjectURL(file) });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) setPreviewImg(URL.createObjectURL(file));
  };

  const handleUploadReceipt = async () => {
    const file = fileInputRef.current?.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('receipt', file);
      const res = await apiFetch(`/api/trades/${id}/receipt`, {
        method: 'POST',
        body: fd,
      });
      if (!res.ok) throw new Error('Error al subir comprobante');
      const data = await res.json();
      setTrade(data.trade);
      setPreviewImg(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setError(err.message);
    }
    setUploading(false);
  };

  const handleRelease = async () => {
    setReleasing(true);
    try {
      const res = await apiFetch(`/api/trades/${id}/release`, {
        method: 'PATCH',
      });
      const updated = await res.json();
      setTrade(updated);
      setShowConfirm(false);
    } catch {}
    setReleasing(false);
  };

  // ===========================
  // CANCELAR TRADE ✅
  // ===========================
  const handleCancel = async () => {
    setCancelling(true);
    try {
      const res = await apiFetch(`/api/trades/${id}/cancel`, {
        method: 'PATCH',
      });
      if (!res.ok) throw new Error('Error al cancelar');
      const updated = await res.json();
      setTrade(updated);
      setShowCancelConfirm(false);
    } catch (err) {
      setError(err.message);
    }
    setCancelling(false);
  };

  // ===========================
  // ABRIR DISPUTA ✅
  // ===========================
  const handleDispute = async () => {
    if (!disputeReason.trim()) { setError('Contanos qué pasó para abrir la disputa.'); return; }
    setDisputing(true);
    try {
      const res = await apiFetch(`/api/trades/${id}/dispute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: disputeReason.trim() }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.message || 'Error al abrir la disputa'); }
      const data = await res.json();
      setTrade(data.trade);
      setShowDispute(false);
      setDisputeReason('');
    } catch (err) {
      setError(err.message);
    }
    setDisputing(false);
  };

  if (loading) return (
    <div className="td-wrapper">
      <div className="td-skel td-skel-head" />
      <div className="td-skel td-skel-steps" />
      <div className="td-skel-body">
        <div className="td-skel-col">
          <div className="td-skel td-skel-card" style={{ height: 76 }} />
          <div className="td-skel td-skel-card" style={{ height: 120 }} />
          <div className="td-skel td-skel-card" style={{ height: 150 }} />
        </div>
        <div className="td-skel td-skel-card td-skel-chat" />
      </div>
    </div>
  );
  if (error)   return <div className="td-loading"><FiAlertTriangle /> {error}</div>;
  if (!trade)  return null;

  const isBuyer     = trade.buyerId  === user?.id;
  const isSeller    = trade.sellerId === user?.id;
  const counterpart = isBuyer ? trade.seller : trade.buyer;
  const st          = STATUS_CONFIG[trade.status] ?? STATUS_CONFIG['CREATED'];
  const currentStep = st.step;
  const avatarColors = ['#0091ea','#00b248','#e53935','#8e24aa','#f4511e','#039be5'];
  const avatarColor  = avatarColors[(counterpart?.name?.charCodeAt(0) ?? 0) % avatarColors.length];

  return (
    <div className="td-wrapper">

      <div className="td-header">
        <button className="td-back" onClick={() => navigate('/trades')}><FiArrowLeft /> Mis trades</button>
        <div className="td-header-info">
          <h2 className="td-title">Trade <span className="td-trade-id fin-num">T-{trade.id}</span></h2>
          <span className="fin-chip td-status-badge" style={{ background: st.color + '15', color: st.color, borderColor: st.color + '40' }}>
            <st.Icon className="td-status-icon" /> {st.label}
          </span>
          <span className={`td-socket-dot ${connected ? 'on' : 'off'}`} title={connected ? 'Chat en vivo' : 'Reconectando...'} />
        </div>
      </div>

      <EscrowBox trade={trade} />

      {currentStep >= 0 && (
        <div className="td-steps">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.label}>
              <div className={`td-step ${i <= currentStep ? 'active' : ''} ${i === currentStep ? 'current' : ''}`}>
                <div className="td-step-dot">{i < currentStep ? <FiCheck /> : <s.Icon />}</div>
                <span className="td-step-label">{s.label}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`td-step-line ${i < currentStep ? 'active' : ''}`} />}
            </React.Fragment>
          ))}
        </div>
      )}

      <div className="td-body">
        <div className="td-info-panel">

          {/* ✅ Timer con callback onExpired */}
          <PaymentTimer
            createdAt={trade.createdAt}
            status={trade.status}
            onExpired={() => setTimerExpired(true)}
          />

          {/* ✅ Banner de tiempo agotado */}
          {timerExpired && trade.status === 'WAITING_PAYMENT' && (
            <div className="td-expired-banner">
              <FiAlertOctagon className="td-expired-icon" />
              <div>
                <strong>Tiempo agotado</strong>
                <p>El comprador no realizó el pago a tiempo. El trade puede cancelarse.</p>
              </div>
            </div>
          )}

          <RoleInstructions isBuyer={isBuyer} status={trade.status} trade={trade} />

          <div className="td-card">
            <h3 className="td-card-title"><FiUser className="td-card-title-icon" /> {isBuyer ? 'Vendedor' : 'Comprador'}</h3>
            <div className="td-counterpart">
              <div className="td-counterpart-avatar" style={{ background: avatarColor }}>
                {counterpart?.avatar
                  ? <img src={counterpart.avatar} alt="avatar" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                  : (counterpart?.name ?? 'U').slice(0, 2).toUpperCase()
                }
              </div>
              <div className="td-counterpart-info">
                <div className="td-counterpart-name">{counterpart?.name ?? '—'}</div>
                <div className="td-counterpart-username">@{counterpart?.username ?? '—'}</div>
                <div className="td-counterpart-stats">
                  <span><FiUsers className="td-mini-icon" /> <span className="fin-num">{counterpart?.trades ?? 0}</span> trades</span>
                  <span><FiStar className="td-mini-icon" /> <span className="fin-num">{counterpart?.xp ?? 0}</span> XP</span>
                </div>
                {counterpart?.username && (
                  <button className="td-viewprofile" onClick={() => navigate(`/profile/${counterpart.username}`)}>
                    <FiUser className="td-mini-icon" /> Ver perfil completo →
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="td-card">
            <h3 className="td-card-title"><FiFileText className="td-card-title-icon" /> Detalles</h3>
            <div className="td-info-grid">
              <div className="td-info-item"><span className="fin-label">Monto</span><strong className="fin-num">USDT {Number(trade.amount).toLocaleString()}</strong></div>
              <div className="td-info-item"><span className="fin-label">Cripto</span><strong>{trade.crypto}</strong></div>
              <div className="td-info-item"><span className="fin-label">Precio ref.</span><strong className="fin-num">${Number(trade.price).toLocaleString()}</strong></div>
              <div className="td-info-item">
                <span className="fin-label">Spread</span>
                <strong className="fin-num" style={{ color: trade.spread <= 0 ? 'var(--fin-up)' : 'var(--fin-down)' }}>
                  {trade.spread === 0 ? 'TRM' : trade.spread > 0 ? `+${trade.spread}%` : `${trade.spread}%`}
                </strong>
              </div>
              <div className="td-info-item"><span className="fin-label">Red</span><strong><span className="fin-chip">{trade.blockchain}</span></strong></div>
              <div className="td-info-item"><span className="fin-label">Pago</span><strong><span className="fin-chip">{trade.payment}</span></strong></div>
            </div>
          </div>

          <div className="td-card">
            <h3 className="td-card-title"><FiShield className="td-card-title-icon" /> Protección del trade</h3>
            <div className={`td-escrow-status ${trade.escrowReleased ? 'unlocked' : trade.escrowLocked ? 'locked' : 'pending'}`}>
              {trade.escrowReleased && <><FiUnlock className="td-escrow-icon" /> El vendedor liberó la cripto</>}
              {trade.escrowLocked && !trade.escrowReleased && <><FiLock className="td-escrow-icon" /> Trade activo — el vendedor libera al confirmar tu pago</>}
              {!trade.escrowLocked && !trade.escrowReleased && <><FiClock className="td-escrow-icon" /> Trade sin iniciar</>}
            </div>
            <p className="td-escrow-note">Operás con un <strong>vendedor verificado</strong> por invitación. En esta fase libera la cripto manualmente al confirmar tu pago; el escrow automático llega en una fase próxima.</p>
          </div>

          {trade.receiptUrl && (
            <div className="td-card">
              <h3 className="td-card-title"><FiFileText className="td-card-title-icon" /> Comprobante</h3>
              <img src={trade.receiptUrl} alt="Comprobante" className="td-receipt-img" onClick={() => window.open(trade.receiptUrl, '_blank')} />
              <p className="td-receipt-hint">Click para ver en tamaño completo</p>
            </div>
          )}

          <div className="td-actions">

            {/* Subir comprobante */}
            {isBuyer && trade.status === 'WAITING_PAYMENT' && !timerExpired && (
              <div className="td-upload-wrap">
                <input ref={fileInputRef} type="file" accept="image/*" className="td-file-input" onChange={handleFileChange} />
                {previewImg ? (
                  <div className="td-preview-wrap">
                    <img src={previewImg} alt="Preview" className="td-preview-img" />
                    <button className="td-btn-paid" onClick={handleUploadReceipt} disabled={uploading}>
                      {uploading ? <><span className="td-btn-spinner" /> Subiendo...</> : <><FiUpload className="td-btn-ic" /> Subir comprobante y notificar</>}
                    </button>
                    <button className="td-btn-cancel" onClick={() => { setPreviewImg(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}>
                      Cancelar
                    </button>
                  </div>
                ) : (
                  <button className="td-btn-paid" onClick={() => fileInputRef.current?.click()}>
                    <FiPaperclip className="td-btn-ic" /> Adjuntar comprobante de pago
                  </button>
                )}
              </div>
            )}

            {/* Liberar fondos */}
            {isSeller && trade.status === 'PAID' && !showConfirm && (
              <button className="td-btn-release" onClick={() => setShowConfirm(true)}>
                <FiUnlock className="td-btn-ic" /> Liberar fondos al comprador
              </button>
            )}

            {showConfirm && (
              <div className="td-confirm td-danger">
                <div className="td-danger-head"><FiAlertTriangle className="td-btn-ic" /> Zona de riesgo</div>
                <p>Vas a <strong>liberar la cripto</strong> al comprador. Confirmá solo si <strong>ya verificaste el pago en tu cuenta</strong>. <span className="td-danger-strong">Esta acción no se puede deshacer.</span></p>
                <div className="td-confirm-btns">
                  <button className="td-btn-release" onClick={handleRelease} disabled={releasing}>
                    {releasing ? <><span className="td-btn-spinner" /> Liberando...</> : <><FiUnlock className="td-btn-ic" /> Sí, liberar fondos</>}
                  </button>
                  <button className="td-btn-cancel" onClick={() => setShowConfirm(false)}>Cancelar</button>
                </div>
              </div>
            )}

            {trade.status === 'COMPLETED' && (
              <div className="td-completed"><FiCheck className="td-btn-ic" /> Trade completado exitosamente — <span className="fin-num">+150 XP</span> ganados</div>
            )}

            {trade.status === 'COMPLETED' && !showReview && (
              <button className="td-btn-review" onClick={() => setShowReview(true)}>
                <FiStar className="td-btn-ic" /> Calificar a {isBuyer ? 'tu vendedor' : 'tu comprador'}
              </button>
            )}
            {trade.status === 'COMPLETED' && showReview && (
              <ReviewFlow
                tradeId={trade.id}
                counterpartyName={counterpart?.username}
                counterpartyId={isBuyer ? trade.sellerId : trade.buyerId}
                onReviewSubmitted={() => setShowReview(false)}
              />
            )}

            {/* ✅ CANCELAR cuando timer expiró */}
            {trade.status === 'WAITING_PAYMENT' && timerExpired && !showCancelConfirm && (
              <button className="td-btn-cancel-trade" onClick={() => setShowCancelConfirm(true)}>
                <FiXCircle className="td-btn-ic" /> Cancelar trade — tiempo agotado
              </button>
            )}

            {showCancelConfirm && (
              <div className="td-cancel-box">
                <div className="td-cancel-title"><FiXCircle className="td-btn-ic" /> Cancelar trade</div>
                <p>El tiempo de pago expiró. Al cancelar, el trade se cerrará y no podrá reabrirse.</p>
                <div className="td-confirm-btns">
                  <button className="td-btn-cancel-trade" onClick={handleCancel} disabled={cancelling}>
                    {cancelling ? <><span className="td-btn-spinner" /> Cancelando...</> : <><FiXCircle className="td-btn-ic" /> Sí, cancelar trade</>}
                  </button>
                  <button className="td-btn-cancel" onClick={() => setShowCancelConfirm(false)}>Volver</button>
                </div>
              </div>
            )}

            {/* Disputa */}
            {(trade.status === 'WAITING_PAYMENT' || trade.status === 'PAID') && !showDispute && !timerExpired && (
              <button className="td-btn-dispute" onClick={() => setShowDispute(true)}>
                <FiAlertTriangle className="td-btn-ic" /> Abrir disputa
              </button>
            )}

            {showDispute && (
              <div className="td-dispute-box">
                <div className="td-dispute-title"><FiAlertTriangle className="td-btn-ic" /> Abrir una disputa</div>
                <p>Si hay un problema con este trade, nuestro equipo lo revisará. Contanos qué pasó:</p>
                <textarea
                  className="td-dispute-textarea"
                  placeholder="Ej: pagué pero el vendedor no libera la cripto; el monto recibido no coincide..."
                  value={disputeReason}
                  onChange={e => { setDisputeReason(e.target.value); setError(null); }}
                  rows={3}
                  maxLength={1000}
                />
                <div className="td-confirm-btns">
                  <button className="td-btn-dispute-confirm" onClick={handleDispute} disabled={disputing || !disputeReason.trim()}>
                    {disputing ? <><span className="td-btn-spinner" /> Abriendo...</> : <><FiAlertTriangle className="td-btn-ic" /> Abrir disputa</>}
                  </button>
                  <button className="td-btn-cancel" onClick={() => { setShowDispute(false); setDisputeReason(''); }}>Cancelar</button>
                </div>
              </div>
            )}

            {/* Trade en disputa — esperando arbitraje del equipo */}
            {trade.status === 'DISPUTED' && (
              <div className="td-dispute-active">
                <FiAlertTriangle className="td-dispute-active-icon" />
                <div>
                  <strong>Trade en disputa</strong>
                  <p>Nuestro equipo está revisando este trade. Te avisaremos cuando se resuelva. Podés seguir aportando información por el chat.</p>
                </div>
              </div>
            )}

            {/* Trade cancelado */}
            {trade.status === 'CANCELLED' && (
              <div className="td-cancelled">
                <span className="td-cancelled-label"><FiXCircle className="td-btn-ic" /> Trade cancelado</span>
                <button className="td-btn-back-mk" onClick={() => navigate('/')}>← Volver al Marketplace</button>
              </div>
            )}

          </div>
        </div>

        <div className="td-chat-panel">
          <div className="td-chat-header">
            <span className="td-chat-title"><FiMessageCircle className="td-chat-title-icon" /> Chat del trade</span>
            <span className={`td-chat-status ${connected ? 'live' : 'polling'}`}>
              <span className={`td-live-dot ${connected ? 'on' : 'off'}`} />
              {connected ? 'En vivo' : 'Reconectando...'}
            </span>
          </div>

          <div className="td-chat-messages" ref={messagesRef} onScroll={onMessagesScroll}>
            {messages.length === 0 && (
              <div className="td-chat-empty">
                <FiMessageCircle className="td-chat-empty-icon" />
                <p className="td-chat-empty-title">Aún no hay mensajes</p>
                <p className="td-chat-empty-sub">Coordiná el pago con {isBuyer ? 'el vendedor' : 'el comprador'} por acá. Compartí comprobantes y dudas — todo queda registrado en el trade.</p>
              </div>
            )}
            {messages.map((msg, idx) => {
              const isMe = msg.senderId === user?.id;
              const showSep = idx === 0 || dayLabel(messages[idx - 1].createdAt) !== dayLabel(msg.createdAt);
              return (
                <React.Fragment key={msg.id}>
                  {showSep && <div className="td-chat-daysep"><span>{dayLabel(msg.createdAt)}</span></div>}
                  <div className={`td-msg ${isMe ? 'me' : 'them'}`}>
                    {!isMe && <div className="td-msg-avatar">{(msg.sender?.name ?? 'U').slice(0, 2).toUpperCase()}</div>}
                    <div className="td-msg-content">
                      {!isMe && <div className="td-msg-sender">{msg.sender?.name}</div>}
                      {msg.imageUrl && <img src={msg.imageUrl} alt="imagen" className="td-msg-image" onClick={() => window.open(msg.imageUrl, '_blank')} />}
                      {msg.content && <div className="td-msg-bubble">{msg.content}</div>}
                      <div className="td-msg-time fin-num">
                        {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
            <div ref={bottomRef} />
          </div>

          {!atBottom && messages.length > 0 && (
            <button className="td-chat-jump" onClick={jumpToLatest}><FiArrowDown /> Últimos mensajes</button>
          )}

          {trade.status !== 'COMPLETED' && trade.status !== 'CANCELLED' ? (
            <div className="td-chat-inputwrap">
              {chatImg && (
                <div className="td-chat-imgchip">
                  <img src={chatImg.url} alt="adjunto" />
                  <span>Imagen lista para enviar</span>
                  <button onClick={() => { setChatImg(null); if (chatFileRef.current) chatFileRef.current.value = ''; }} aria-label="Quitar imagen"><FiX /></button>
                </div>
              )}
              <div className="td-chat-input">
                <input ref={chatFileRef} type="file" accept="image/*" className="td-file-input" onChange={handleChatFile} />
                <button className="td-chat-attach" onClick={() => chatFileRef.current?.click()} title="Adjuntar imagen"><FiImage /></button>
                <PaymentAccountPicker onPick={(txt) => setText(t => t.trim() ? `${t.trim()}\n${txt}` : txt)} />
                <textarea
                  className="td-chat-textarea"
                  placeholder="Escribí un mensaje... (Enter para enviar)"
                  value={text}
                  onChange={e => setText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={2}
                />
                <button className="td-chat-send" onClick={handleSend} disabled={sending || (!text.trim() && !chatImg)}>
                  {sending ? <span className="td-btn-spinner" /> : <FiSend />}
                </button>
              </div>
            </div>
          ) : (
            <div className={`td-chat-closed ${trade.status === 'CANCELLED' ? 'is-cancelled' : 'is-done'}`}>
              <span className="td-chat-closed-ic">{trade.status === 'COMPLETED' ? <FiCheck /> : <FiX />}</span>
              <div className="td-chat-closed-text">
                <strong>{trade.status === 'COMPLETED' ? 'Trade completado' : 'Trade cancelado'}</strong>
                <span>El chat quedó cerrado. El historial sigue disponible aquí arriba.</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
